/**
 * apps/web/src/i18n/LanguageProvider.tsx
 *
 * Runtime UI-language context. Exposes:
 *   - useLang():    { lang, setLang } - read/switch the active language
 *   - useStrings(): the active dictionary (re-renders consumers on a switch)
 *
 * Language resolution (highest precedence first):
 *   1. localStorage override  - the user's explicit choice, set from Settings
 *   2. workspace.locale        - the server-persisted, cross-device default
 *   3. 'it'                     - product default
 *
 * Switching the language here updates state + localStorage + the formatters
 * (setFormatLocale). Persisting the choice to the server (PATCH /workspaces) is
 * done by the Settings card; this provider keeps the client in sync.
 *
 * Mounted under <AuthProvider> so it can read workspace.locale.
 */

import type React from 'react';
import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useAuth } from '../auth/AuthContext';
import { setFormatLocale } from '../lib/format';
import { dictionaries, type Lang, type Strings } from '../lib/strings';

const STORAGE_KEY = 'glarno.lang';

/** Map a BCP-47 locale (it-IT, en-US) or short code to a supported Lang. */
export function normalizeLang(locale: string | null | undefined): Lang {
  if (!locale) return 'it';
  return locale.toLowerCase().startsWith('en') ? 'en' : 'it';
}

function readStoredLang(): Lang | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === 'en' || value === 'it' ? value : null;
  } catch {
    return null;
  }
}

function writeStoredLang(lang: Lang): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // Private mode / storage disabled: language still works for this session.
  }
}

interface LanguageContextValue {
  readonly lang: Lang;
  readonly strings: Strings;
  /** Switch the active UI language (updates state, localStorage, formatters). */
  readonly setLang: (lang: Lang) => void;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

export function LanguageProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const { workspace } = useAuth();

  // Initial language: stored override wins; otherwise default to Italian until
  // the workspace locale loads (synced in the effect below).
  const [lang, setLangState] = useState<Lang>(() => readStoredLang() ?? 'it');

  // Keep the formatters aligned with the active language on every change.
  useEffect(() => {
    setFormatLocale(lang);
  }, [lang]);

  // When there is no explicit local override, follow the workspace locale once
  // the session loads (and when it changes, e.g. after switching workspace).
  useEffect(() => {
    if (readStoredLang() !== null) return;
    if (!workspace?.locale) return;
    setLangState(normalizeLang(workspace.locale));
  }, [workspace?.locale]);

  const setLang = useCallback((next: Lang): void => {
    writeStoredLang(next);
    setFormatLocale(next);
    setLangState(next);
  }, []);

  const value = useMemo<LanguageContextValue>(
    () => ({ lang, strings: dictionaries[lang], setLang }),
    [lang, setLang],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

// Fallback used when a component renders outside <LanguageProvider> (e.g. in
// isolated unit tests). It returns the Italian dictionary - the same copy the
// app shipped before i18n - with a no-op setter, so tests need no wrapping.
const FALLBACK: LanguageContextValue = {
  lang: 'it',
  strings: dictionaries.it,
  setLang: () => {},
};

function useLanguageContext(): LanguageContextValue {
  return useContext(LanguageContext) ?? FALLBACK;
}

/** Read or switch the active UI language. */
export function useLang(): { lang: Lang; setLang: (lang: Lang) => void } {
  const { lang, setLang } = useLanguageContext();
  return { lang, setLang };
}

/** The active dictionary. Re-renders the caller when the language switches. */
export function useStrings(): Strings {
  return useLanguageContext().strings;
}
